import { apiClient } from './base';

export interface Company {
  companyid: string;
  companyname: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  contactperson: string;
  isactive: boolean;
  createtime: string;
  timezone: string;
  companytype: string;
  prefixword: string;
  reportprefix: string;
  reportno: string;
  logo?: string;
  strengthformat?: string;
}

export interface CompanyCreate {
  companyname: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  contactperson?: string;
  isactive?: boolean;
  timezone?: string;
  companytype: string;
  prefixword?: string;
  reportprefix?: string;
}

export interface CompanyUpdate {
  companyname?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  contactperson?: string;
  isactive?: boolean;
  timezone?: string;
  companytype?: string;
  prefixword?: string;
  reportprefix?: string;
}

export interface CompanyUser {
  userid: string;
  username: string;
  emailaddress?: string;
  firstname?: string;
  lastname?: string;
  jobtitle?: string;
  mobile?: string;
  phone?: string;
  usertype: string;
  usercategory?: string;
  enable: string;
  companyid: string;
  full_name?: string;
  is_enabled: boolean;
  user_type_name: string;
}

/**
 * Companies API service
 */
export const companiesAPI = {
  /**
   * Get all companies
   */
  getCompanies: async (): Promise<Company[]> => {
    const response = await apiClient.get('/companies/');
    return response.data;
  },
  
  /**
   * Get company by ID
   */
  getCompanyById: async (companyId: string): Promise<Company> => {
    const response = await apiClient.get(`/companies/${companyId}`);
    return response.data;
  },
  
  /**
   * Create new company
   */
  createCompany: async (companyData: CompanyCreate): Promise<Company> => {
    const response = await apiClient.post('/companies/', companyData);
    return response.data;
  },
  
  /**
   * Update company
   */
  updateCompany: async (companyId: string, companyData: CompanyUpdate): Promise<Company> => {
    const response = await apiClient.put(`/companies/${companyId}`, companyData);
    return response.data;
  },
  
  /**
   * Delete company
   */
  deleteCompany: async (companyId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}`);
  },

  /**
   * Get users for a specific company
   */
  getCompanyUsers: async (companyId: string): Promise<CompanyUser[]> => {
    const response = await apiClient.get(`/companies/${companyId}/users`);
    return response.data;
  },

  /**
   * Upload company logo
   */
  uploadLogo: async (companyId: string, file: File): Promise<{ filename: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/files/companies/${companyId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Login as a specific user (Super Admin only)
   */
  loginAsUser: async (userId: string): Promise<{ access_token: string; token_type: string; user: CompanyUser }> => {
    const response = await apiClient.post('/auth/login-as-user', { user_id: userId });
    return response.data;
  }
};